// SEO.tsx
// Reusable SEO + structured data component using react-helmet-async.
// Provides: <title>, meta description, canonical, robots, Open Graph, Twitter Card,
// and optional JSON-LD (array or single object). Pages pass semantic props only.
// Design choices:
// - Centralizes tag generation to avoid duplication.
// - Accepts dynamic jsonLd so data-driven pages (e.g. Pricing) can supply OfferCatalog/Offers.
// - Adds default site-wide fallbacks but requires explicit title/description for EEAT clarity.
// - Respects noindex for internal / protected pages.
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'TradeSagePro';
// Primary domain is now www
const APEX_URL = 'https://tradesagepro.com';
const SITE_URL = 'https://www.tradesagepro.com';
const DEFAULT_IMAGE = SITE_URL + '/favicon.svg';
const THEME_COLOR = '#0b1220';

export interface SEOProps {
  title: string; // Human-readable, keyword-rich but concise (50–60 chars).
  description: string; // 150–160 chars recommended for SERP snippet.
  canonical?: string; // Absolute URL. Fallback: SITE_URL + current pathname (client only).
  image?: string; // Absolute URL of social share image.
  noindex?: boolean; // For private / thin / duplicate pages.
  nofollow?: boolean; // Optionally block link equity passing on internal/private pages.
  robotsOverride?: string; // Full robots string override (e.g. 'noindex,nofollow,max-snippet:0').
  type?: 'website' | 'article' | 'product' | 'faq' | 'offer' | 'profile' | 'legal'; // OG type hint.
  jsonLd?: Record<string, any> | Array<Record<string, any>>; // Structured data graph(s).
  children?: React.ReactNode; // Allow page-level extensions if needed.
}

function normalizeUrl(u?: string) {
  if (!u) return u;
  try {
    return u.replace(APEX_URL, SITE_URL);
  } catch {
    return u;
  }
}

function resolveCanonical(explicit?: string) {
  if (explicit) return normalizeUrl(explicit)!;
  if (typeof window !== 'undefined') {
    try {
      const p = window.location.pathname || '/';
      if (p === '/' || p === '') return SITE_URL + '/';
      return SITE_URL + p.replace(/\/$/, '');
    } catch {}
  }
  return SITE_URL + '/';
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  image,
  noindex,
  nofollow,
  robotsOverride,
  type = 'website',
  jsonLd,
  children
}) => {
  const canonicalUrl = resolveCanonical(canonical);
  const shareImage = normalizeUrl(image) || DEFAULT_IMAGE;
  // Ensure description trimmed for meta.
  const metaDescription = description.trim();
  const robots = robotsOverride
    ? robotsOverride
    : [noindex ? 'noindex' : 'index', nofollow ? 'nofollow' : 'follow'].join(', ');
  // Normalize JSON-LD input to array.
  const jsonListRaw: Array<Record<string, any>> = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];
  // Normalize any apex-domain URLs inside JSON-LD by string replace.
  const jsonList: Array<Record<string, any>> = jsonListRaw.map((obj) => {
    try {
      const s = JSON.stringify(obj);
      const replaced = s.split(APEX_URL).join(SITE_URL);
      return JSON.parse(replaced);
    } catch {
      return obj;
    }
  });

  const ogType = (() => {
    if (type === 'article') return 'article';
    if (type === 'profile') return 'profile';
    if (type === 'product' || type === 'offer') return 'product';
    return 'website';
  })();

  return (
    <>
      <Helmet prioritizeSeoTags>
        {/* Primary tags */}
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={robots} />
        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:image:alt" content={title} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:site" content="@TradeSagePro" />
        {/* Additional quality / EEAT signals */}
        <meta name="application-name" content={SITE_NAME} />
        <meta name="theme-color" content={THEME_COLOR} />
        {/* JSON-LD structured data */}
        {jsonList.map((obj, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
      </Helmet>
      {children}
    </>
  );
};

export default SEO;
